import CoreUtils from '../../utils/coreUtils.js';
import Utils3D from '../utils/utils3D.js';
import VoxelGrid from './voxelGrid.js';
import GeoUtils from './geoUtils.js';
import Utils2D from '../../utils/utils2D.js';

const EPSILON = 1e-4;
const MIN_FACE_AREA = 1e-4;

const POS_X_AXIS = new BABYLON.Vector3(1, 0, 0);
const POS_Y_AXIS = new BABYLON.Vector3(0, 1, 0);
const POS_Z_AXIS = new BABYLON.Vector3(0, 0, 1);

// This class allows efficient and convenient queries about mesh connectivity, intersections,
// belonging, etc.
export default class MeshInfo
{
	constructor(mesh, fMinAreaSize = MIN_FACE_AREA)
	{
		this.bboxMin = BABYLON.Vector3.Zero();
		this.bboxMax = BABYLON.Vector3.Zero();
		this.meshFaces = MeshInfo.buildMeshFaceInfo(mesh, fMinAreaSize);

		this.faceVoxelGrid = null;

		this.computeBBox();
	}

	static createFromOtherInfo(otherMeshInfo, funcFilter)
	{
		// Note that this makes a shallow copy:
		let pNewInfo = new MeshInfo(null);
		for(let faceInfo of otherMeshInfo.meshFaces)
		{
			if(funcFilter(faceInfo))
				pNewInfo.meshFaces.push(faceInfo);
		}

		pNewInfo.computeBBox();

		return pNewInfo;
	}

	// Internal
	static buildMeshFaceInfo(pSrcMesh, fMinAreaSize = MIN_FACE_AREA)
	{
		if(!pSrcMesh)
			return [];

		pSrcMesh.computeWorldMatrix();

		let positions = pSrcMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
		let indices = pSrcMesh.getIndices();

		let faceSide1 = BABYLON.Vector3.Zero();
		let faceSide2 = BABYLON.Vector3.Zero();
		let faceSide3 = BABYLON.Vector3.Zero();
		let faceNormal = BABYLON.Vector3.Zero();

		let currWorldMatrix = pSrcMesh.getWorldMatrix();

		// Gather faces into easier format and pre-compute the info we need
		let allMeshInfos = [];
		let faceArea;
		let vecLocalPointPositions = [BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero()];
		let vecGlobalPointPositions = [BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero()];
		for(let index = 0; index < indices.length; index += 3)
		{
			for(let subIndex = 0; subIndex < 3; subIndex++)
			{
				let pointIndex = indices[index + subIndex];
				vecLocalPointPositions[subIndex].copyFromFloats(positions[pointIndex*3], positions[pointIndex*3 + 1], positions[pointIndex*3 + 2]);
				BABYLON.Vector3.TransformCoordinatesToRef(vecLocalPointPositions[subIndex], currWorldMatrix, vecGlobalPointPositions[subIndex]);
			}

			vecGlobalPointPositions[1].subtractToRef(vecGlobalPointPositions[0], faceSide1);
			vecGlobalPointPositions[2].subtractToRef(vecGlobalPointPositions[1], faceSide2);
			BABYLON.Vector3.CrossToRef(faceSide2, faceSide1, faceNormal);
			faceArea = faceNormal.length()*0.5;

			// Skip bad faces where the vertices are the same
			if(faceArea < fMinAreaSize)
				continue;

			faceNormal.normalize();
			vecGlobalPointPositions[0].subtractToRef(vecGlobalPointPositions[2], faceSide3);

			let sideLengths = [faceSide1.length(), faceSide2.length(), faceSide3.length()];

			faceSide1.normalize();
			faceSide2.normalize();
			faceSide3.normalize();

			let currInfo = {
				localVerts: vecLocalPointPositions.map((vecPoint) => { return vecPoint.clone(); }),
				globalVerts: vecGlobalPointPositions.map((vecPoint) => { return vecPoint.clone(); }),
				normal: faceNormal.clone(),
				index: index,
				sideDirs: [faceSide1.clone(), faceSide2.clone(), faceSide3.clone()],
				sideLengths: sideLengths,
				area: faceArea,

				meshId: pSrcMesh.uniqueId,
			};

			allMeshInfos.push(currInfo);
		}

		return allMeshInfos;
	}

	getBBox(bLocal = false)
	{
		let bboxMin = new BABYLON.Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
		let bboxMax = new BABYLON.Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);

		const vertBufferName = bLocal ? 'localVerts' : 'globalVerts';
		for(let faceInfo of this.meshFaces)
		{
			for(let subIndex = 0; subIndex < 3; subIndex++)
				Utils3D.includePoint(faceInfo[vertBufferName][subIndex], bboxMin, bboxMax);
		}

		return { minimumWorld: bboxMin, maximumWorld: bboxMax };
	}

	computeBBox()
	{
		let bbox = this.getBBox();
		this.bboxMin = bbox.minimumWorld;
		this.bboxMax = bbox.maximumWorld;
	}

	computeAdjacentFaces(optEpsilon = EPSILON, fMinOverlapPerc = 0.0, bExact = false, bUseVoxelGrid = true)
	{
		for(let faceInfo of this.meshFaces)
		{
			faceInfo.adjacentFaces = [];
			for(let subIndex = 0; subIndex < 3; subIndex++)
			{
				if(bExact)
					faceInfo.adjacentFaces[subIndex] = this.getAdjacentFaceExact(faceInfo.globalVerts[subIndex], faceInfo.globalVerts[(subIndex + 1) % 3], faceInfo.index, null, optEpsilon, bUseVoxelGrid);
				else
				{
					faceInfo.adjacentFaces[subIndex] = this.getAdjacentFaces(faceInfo.globalVerts[subIndex], faceInfo.globalVerts[(subIndex + 1) % 3], faceInfo.index, null, optEpsilon, bUseVoxelGrid);
					if(fMinOverlapPerc > 0)
						faceInfo.adjacentFaces[subIndex] = faceInfo.adjacentFaces[subIndex].filter((info) => { return info.overlap > fMinOverlapPerc; });
				}

				// Sort by overlap
				faceInfo.adjacentFaces[subIndex].sort((info1, info2) => { return info2.overlap - info1.overlap; });
			}
		}
	}

	getAdjacentFace(faceInfo, subIndex, traversedFacesSet = null, mustIncludePoint = null, optEpsilon = -1e-6)
	{
		for(let adjFace of faceInfo.adjacentFaces[subIndex])
		{
			if(traversedFacesSet && traversedFacesSet.has(adjFace.info.index))
				continue;

			if(mustIncludePoint && !Utils3D.isPointInTriangle(mustIncludePoint, adjFace.info.globalVerts, false, optEpsilon))
				continue;

			return adjFace.info;
		}

		return null;
	}

	getSize()
	{
		return this.bboxMax.subtract(this.bboxMin);
	}

	computeFaceVoxelGrid(fPaddingPerc = 0.05, optSubdivs = 16)
	{
		return this.getFaceVoxelGrid(fPaddingPerc);
	}

	getFaceVoxelGrid(fPaddingPerc = 0.05, optSubdivs = 16)
	{
		if(!this.faceVoxelGrid)
		{
			let vecVoxelGridPadding = this.getSize().scale(fPaddingPerc);

			this.faceVoxelGrid = new VoxelGrid(this.bboxMin.subtract(vecVoxelGridPadding), this.bboxMax.add(vecVoxelGridPadding), optSubdivs);
			this.faceVoxelGrid.setUniqueFieldName('index');
			for(let faceInfo of this.meshFaces)
				this.faceVoxelGrid.addFace(faceInfo.globalVerts, faceInfo);
		}

		return this.faceVoxelGrid;
	}

	static getIndexForPoint(faceInfo, vecPoint)
	{
		for(let idx = 0; idx < 3; idx++)
		{
			if(faceInfo.globalVerts[idx].subtract(vecPoint).lengthSquared() < 1e-12)
				return idx;
		}

		return -1;
	}

	static generateLocalFaceVoxelGrid(pMesh, fPaddingPerc = 0.05, optSubdivs = 16)
	{
		pMesh.computeWorldMatrix();

		let positions = pMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
		let indices = pMesh.getIndices();

		let bbox = pMesh.getBoundingInfo().boundingBox;
		let localSize = bbox.maximum.subtract(bbox.minimum);
		let vecVoxelGridPadding = localSize.scale(fPaddingPerc);

		//const startTime = new Date().getTime();
		//CoreUtils.log(`Began creating voxel grid for mesh ${pMesh.name} num indices = ${indices.length}`);

		let faceVoxelGrid = new VoxelGrid(bbox.minimum.subtract(vecVoxelGridPadding), bbox.maximum.add(vecVoxelGridPadding), optSubdivs);
		faceVoxelGrid.setUniqueFieldName('index');
		let localVerts = [BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero()];
		for(let index = 0; index < indices.length; index += 3)
		{
			let faceInfo = {
				index: index,
			};
			for(let subIndex = 0; subIndex < 3; subIndex++)
			{
				let pointIndex = indices[index + subIndex];
				localVerts[subIndex].copyFromFloats(positions[pointIndex*3], positions[pointIndex*3 + 1], positions[pointIndex*3 + 2]);
			}
			faceVoxelGrid.addFace(localVerts, faceInfo);
		}

		//let endTime = new Date().getTime();
		//let totalTime = (endTime - startTime)/1000.0;
		//CoreUtils.log(`Done creating voxel grid, time = ${totalTime}s`);

		return faceVoxelGrid;
	}

	getFaceInfos()
	{
		return this.meshFaces;
	}

	getFaceContainingPoint(vecPosition, ignoredFaces = null, optEpsilon = EPSILON)
	{
		for(let faceInfo of this.meshFaces)
		{
			if(ignoredFaces && ignoredFaces.has(faceInfo.index))
				continue;

			if(Utils3D.isPointInTriangle(vecPosition, faceInfo.globalVerts, false, -1e-6))
				return faceInfo;
		}

		return null;
	}

	getEdgesContainingPoint(vecPosition, excludedFacesSet = null, bOnlyTheFirstOne = false, optEpsilon = undefined, bUseVoxelGrid = true)
	{
		let meshFaces = this.meshFaces;
		if(bUseVoxelGrid)
		{
			let voxelGrid = this.getFaceVoxelGrid();
			meshFaces = voxelGrid.getObjectsNear(vecPosition, optEpsilon ? optEpsilon*2 : EPSILON*2);
		}

		let pRes = [];
		for(let faceInfo of meshFaces)
		{
			if(excludedFacesSet && excludedFacesSet.has(faceInfo.index))
				continue;

			for(let subIdx = 0; subIdx < 3; subIdx++)
			{
				//if(Utils3D.doesSegmentContainPoint(faceInfo.globalVerts[subIdx], faceInfo.globalVerts[(subIdx + 1) % 3], vecPosition, optEpsilon))
				if(Utils3D.doesSegmentContainPoint(faceInfo.globalVerts[subIdx], faceInfo.sideDirs[subIdx], faceInfo.sideLengths[subIdx], vecPosition, optEpsilon))
				{
					pRes.push({ faceInfo: faceInfo, vertexIndex: subIdx });
					if(bOnlyTheFirstOne)
						return pRes;
				}
			}
		}

		return pRes;
	}

	detectIdenticalFaces()
	{
		// Note: this function is for debugging only, it's very slow.
		let iNumIdenticalFaces = 0;
		for(let faceInfo of this.meshFaces)
		{
			let identicalFaces = [];
			for(let innerFaceInfo of this.meshFaces)
			{
				// See if the faces are actually identical:
				if(Utils3D.areTrianglesIdentical(faceInfo.globalVerts, innerFaceInfo.globalVerts))
					identicalFaces.push(innerFaceInfo);
			}

			if(identicalFaces.length > 1)
			{
				// Detected identical faces!
				console.log(`Identical faces detected!, area = ${identicalFaces[0].area}`);
				iNumIdenticalFaces++;
			}
		}

		return iNumIdenticalFaces;
	}

	getFacesWithVertex(vecPosition, bUseVoxelGrid = true, optEpsilon = EPSILON)
	{
		let meshFaces = this.meshFaces;
		if(bUseVoxelGrid)
		{
			let voxelGrid = this.getFaceVoxelGrid();
			meshFaces = voxelGrid.getObjectsNear(vecPosition, EPSILON*2);
		}

		let vecDist = BABYLON.Vector3.Zero();
		let pRes = [];
		for(let faceInfo of meshFaces)
		{
			for(let subIdx = 0; subIdx < 3; subIdx++)
			{
				vecPosition.subtractToRef(faceInfo.globalVerts[subIdx], vecDist);
				if(vecDist.lengthSquared() < optEpsilon*optEpsilon)
				{
					pRes.push(faceInfo);
					break;
				}
			}
		}

		return pRes;
	}

	getMeshFaceClosestTo(vecPoint)
	{
		let vecProjectedPos = BABYLON.Vector3.Zero();
		let tempVec = BABYLON.Vector3.Zero();
		let closestInfo = null;
		let fClosestDist = Number.MAX_VALUE;
		for(let currInfo of this.meshFaces)
		{
			if(!Utils3D.isPointInTriangle(vecPoint, currInfo.globalVerts, true))
				continue;

			vecPoint.subtractToRef(currInfo.globalVerts[0], tempVec);
			let fSignedDist = BABYLON.Vector3.Dot(currInfo.normal, tempVec);
			let fCurrDist = Math.abs(fSignedDist);
			if(fCurrDist < fClosestDist)
			{
				fClosestDist = fCurrDist;
				closestInfo = currInfo;

				vecPoint.subtractToRef(currInfo.normal.scale(fSignedDist), vecProjectedPos);
			}
		}

		return { info: closestInfo, projectedPoint: vecProjectedPos };
	}

	getClosestIntersectedFace(vecStart, vecDir, fMaxLength = undefined)
	{
		let closestInfo = null;
		let fClosestDist = Number.MAX_VALUE;

		let ray = new BABYLON.Ray(vecStart, vecDir, fMaxLength);

		for(let currInfo of this.meshFaces)
		{
			let pIntersectInfo = ray.intersectsTriangle(currInfo.globalVerts[0], currInfo.globalVerts[1], currInfo.globalVerts[2]);
			if(!pIntersectInfo || pIntersectInfo.distance < EPSILON)
				continue;

			if(fMaxLength && pIntersectInfo.distance > fMaxLength)
				continue;

			if(pIntersectInfo.distance < fClosestDist)
			{
				fClosestDist = pIntersectInfo.distance;
				closestInfo = currInfo;
			}
		}

		return { info: closestInfo, distance: fClosestDist };
	}


	// This method attempts to find adjance faces even if they don't share the full edge,
	// but rather are only partially overlapping or are located together in space without
	// being properly connected through shared vertices. The reason for its existence
	// is non-manifold meshes we get from boolean opeartors.
	getAdjacentFaces(vecStart, vecEnd, iExcludeIndex = -1, traversedFacesSet = null, optEpsilon = EPSILON, bUseVoxelGrid = true)
	{
		let vecSegDir = vecEnd.subtract(vecStart);
		let fOrigLen = vecSegDir.length();
		vecSegDir.normalize();

		let resFaces = [];

		let vecCurrDir = BABYLON.Vector3.Zero();
		let VEC3_DOT = BABYLON.Vector3.Dot;

		let meshFaces = this.meshFaces;
		if(bUseVoxelGrid)
		{
			let voxelGrid = this.getFaceVoxelGrid();
			meshFaces = voxelGrid.getObjectsForSegment(vecStart, vecEnd);
		}
		for(let meshFace of meshFaces)
		{
			if(meshFace.index === iExcludeIndex)
				continue;

			if(traversedFacesSet && traversedFacesSet.has(meshFace.index))
				continue;

			for(let subIndex = 0; subIndex < 3; subIndex++)
			{
				//let bContainsFirstPoint = Utils3D.doesSegmentContainPoint(meshFace.globalVerts[subIndex], meshFace.globalVerts[(subIndex + 1) % 3], vecStart, optEpsilon);
				let bContainsFirstPoint = Utils3D.doesSegmentContainPoint(meshFace.globalVerts[subIndex], meshFace.sideDirs[subIndex], meshFace.sideLengths[subIndex], vecStart, optEpsilon);

				if(!bContainsFirstPoint)
				{
					//let bContainsSecondPoint = Utils3D.doesSegmentContainPoint(meshFace.globalVerts[subIndex], meshFace.globalVerts[(subIndex + 1) % 3], vecEnd, optEpsilon);
					let bContainsSecondPoint = Utils3D.doesSegmentContainPoint(meshFace.globalVerts[subIndex], meshFace.sideDirs[subIndex], meshFace.sideLengths[subIndex], vecEnd, optEpsilon);
					if(!bContainsSecondPoint)
						continue;
				}
		
				// Otherwise, see if the directions are the same:
				meshFace.globalVerts[(subIndex + 1) % 3].subtractToRef(meshFace.globalVerts[subIndex], vecCurrDir);
				vecCurrDir.normalize();

				let fDot = Math.abs(VEC3_DOT(vecSegDir, vecCurrDir));
				// Separate epsilon for angles
				if(Math.abs(fDot - 1.0) >= 1e-7)
					continue;

				// Now, we need to pick the segment with the most overlap of the original segment
				meshFace.globalVerts[(subIndex + 1) % 3].subtractToRef(vecStart, vecCurrDir);
				let fDot1 = VEC3_DOT(vecSegDir, vecCurrDir);
				meshFace.globalVerts[subIndex].subtractToRef(vecStart, vecCurrDir);
				let fDot2 = VEC3_DOT(vecSegDir, vecCurrDir);

				if(fDot2 < fDot1)
				{
					let fTemp = fDot2;
					fDot2 = fDot1;
					fDot1 = fTemp;
				}

				fDot1 = CoreUtils.clamp(fDot1, -optEpsilon, fOrigLen + optEpsilon);
				fDot2 = CoreUtils.clamp(fDot2, -optEpsilon, fOrigLen + optEpsilon);

				let fCurrPerc = (fDot2 - fDot1)/fOrigLen;
				if(fCurrPerc > 0)
					resFaces.push({ info: meshFace, overlap: fCurrPerc });
			}

		}

		return resFaces;
	}

	// This method, unlike the above one, searches for edges that share both ends within the
	// given epsilon. It will not work well for non-manifold meshes.
	getAdjacentFaceExact(vecStart, vecEnd, iExcludeIndex = -1, traversedFacesSet = null, optEpsilon = EPSILON, bUseVoxelGrid = true)
	{
		// Some meshes coming out of booleans are actually very imprecise for some reason, where
		// "adjacent" edges are at this distance... Which is why we use the 1-e3 epsilon.
		const epsilonSquared = optEpsilon*optEpsilon;
		const distSquaredFunc = BABYLON.Vector3.DistanceSquared;
		let meshFaces = this.meshFaces;
		if(bUseVoxelGrid)
		{
			let voxelGrid = this.getFaceVoxelGrid();
			meshFaces = voxelGrid.getObjectsForSegment(vecStart, vecEnd);
		}
		for(let meshFace of meshFaces)
		{
			if(meshFace.index === iExcludeIndex)
				continue;

			if(traversedFacesSet && traversedFacesSet.has(meshFace.index))
				continue;

			for(let subIndex = 0; subIndex < 3; subIndex++)
			{
				let bDoMatchOne = distSquaredFunc(meshFace.globalVerts[subIndex], vecStart) < epsilonSquared && 
									distSquaredFunc(meshFace.globalVerts[(subIndex + 1) % 3], vecEnd) < epsilonSquared;

				let bDoMatchTwo = !bDoMatchOne && distSquaredFunc(meshFace.globalVerts[subIndex], vecEnd) < epsilonSquared && 
									distSquaredFunc(meshFace.globalVerts[(subIndex + 1) % 3], vecStart) < epsilonSquared;

				// This is to match the non-exact version
				if(bDoMatchTwo || bDoMatchOne)
					return [{ info: meshFace, overlap: 1.0 }];
			}
		}

		return [];
	}

	getIntersectedEdges(vecStart, vecEnd, excludedFacesSet = null, optEpsilon = undefined)
	{
		let pRes = [];

		if(!optEpsilon)
			optEpsilon = EPSILON;

		let vecDir = vecEnd.subtract(vecStart);
		let fLength = vecDir.length();
		vecDir.normalize();
		let testRay = new BABYLON.Ray(vecStart, vecDir, fLength);
	
		for(let faceInfo of this.meshFaces)
		{
			if(excludedFacesSet && excludedFacesSet.has(faceInfo.index))
				continue;

			for(let subIdx = 0; subIdx < 3; subIdx++)
			{
				let fCurrIntersectDist = testRay.intersectionSegment(faceInfo.globalVerts[subIdx], faceInfo.globalVerts[(subIdx + 1) % 3], optEpsilon);
				if(fCurrIntersectDist > optEpsilon && fCurrIntersectDist < fLength - optEpsilon)
					pRes.push({ faceInfo: faceInfo, vertexIndex: subIdx, point: testRay.origin.add(testRay.direction.scale(fCurrIntersectDist)) });
			}
		}

		return pRes;
	}

	doesSegmentIntersectFaces(vecSegmentStart, vecSegmentEnd, excludedFaceIds = null)
	{
		let vecDir = vecSegmentEnd.subtract(vecSegmentStart);
		let fLength = vecDir.length();
		vecDir.normalize();
		let testRay = new BABYLON.Ray(vecSegmentStart, vecDir, fLength);
		for(let meshFace of this.meshFaces)
		{
			if(excludedFaceIds && excludedFaceIds.indexOf(meshFace.index) >= 0)
				continue;

			// Either some point is in some triangle,
			// or the segment intersects a face edge.
			if(Utils3D.isPointInTriangle(vecSegmentStart, meshFace.globalVerts)
				|| Utils3D.isPointInTriangle(vecSegmentEnd, meshFace.globalVerts))
				return true;

			for(let subIndex = 0; subIndex < 3; subIndex++)
			{
				let fIntersectDist = testRay.intersectionSegment(meshFace.globalVerts[subIndex], meshFace.globalVerts[(subIndex + 1) % 3], EPSILON);
				if(fIntersectDist > EPSILON && fIntersectDist < fLength - EPSILON)
					return true;
			}

			let pIntersectInfo = testRay.intersectsTriangle(meshFace.globalVerts[0], meshFace.globalVerts[1], meshFace.globalVerts[2]);
			if(pIntersectInfo && pIntersectInfo.distance >= 0.0 && pIntersectInfo.distance <= fLength)
				return true;
		}

		return false;
	}
	
	getIntersectedFaces(vecBBoxMin, vecBBoxMax, optExcludeFaceIndex = -1)
	{
		let resFaces = [];
		let meshFaces = this.meshFaces;
		if(this.faceVoxelGrid)
			meshFaces = this.faceVoxelGrid.getObjectsForSegment(vecBBoxMin, vecBBoxMax);
		for(let meshFace of meshFaces)
		{
			if(meshFace.index === optExcludeFaceIndex)
				continue;

			if(Utils3D.doesBBoxIntersectFace(vecBBoxMin, vecBBoxMax, meshFace))
				resFaces.push(meshFace);
		}

		return resFaces;
	}

	getOBBIntersectedFaces(obb)
	{
		let resFaces = [];
		let meshFaces = this.meshFaces;
		if(this.faceVoxelGrid)
		{
			let worldBBox = obb.getAABBox();
			meshFaces = this.faceVoxelGrid.getObjectsForSegment(worldBBox.minimumWorld, worldBBox.maximumWorld);
		}

		let localBBox = obb.getLocalBBox(EPSILON);

		let vecLocalPointPositions = [BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero()];
		let invWorldMatrix = obb.getWorldMatrix().clone();
		invWorldMatrix.invert();
		for(let meshFace of meshFaces)
		{
			BABYLON.Vector3.TransformCoordinatesToRef(meshFace.globalVerts[0], invWorldMatrix, vecLocalPointPositions[0]);
			BABYLON.Vector3.TransformCoordinatesToRef(meshFace.globalVerts[1], invWorldMatrix, vecLocalPointPositions[1]);
			BABYLON.Vector3.TransformCoordinatesToRef(meshFace.globalVerts[2], invWorldMatrix, vecLocalPointPositions[2]);
			if(Utils3D.doesBBoxIntersectFace(localBBox.minimumWorld, localBBox.maximumWorld, vecLocalPointPositions)) {
				resFaces.push(meshFace);
			}
		}

		return resFaces;
	}

	getFlatFaceGroups(fCreaseAngle = 25)
	{
		const START_FACE_MAX_ANGLE_DEG_DIFF = 70;
		const LAST_FACE_MAX_ANGLE_DEG_DIFF = fCreaseAngle;
		const MIN_TEST_FACE_AREA = 1e-5;

		const fAverageNormalMaxFaceAngleCos = Math.cos(START_FACE_MAX_ANGLE_DEG_DIFF/180.0*Math.PI);
		const fLastMaxFaceAngleCos = Math.cos(LAST_FACE_MAX_ANGLE_DEG_DIFF/180.0*Math.PI);

		this.computeAdjacentFaces();

		// Sort
		let sortedFaceArray = this.meshFaces.slice();
		sortedFaceArray.sort((info1, info2) => {
			// See how much we align with the three major axes, weighted by
			// face area. This allows us to start from largest, most aligned
			// faces first.
			let fMaxDot1 = Math.max(
				Math.abs(BABYLON.Vector3.Dot(info1.normal, POS_X_AXIS)),
				Math.abs(BABYLON.Vector3.Dot(info1.normal, POS_Y_AXIS)),
				Math.abs(BABYLON.Vector3.Dot(info1.normal, POS_Z_AXIS)),
			)*info1.area;

			let fMaxDot2 = Math.max(
				Math.abs(BABYLON.Vector3.Dot(info2.normal, POS_X_AXIS)),
				Math.abs(BABYLON.Vector3.Dot(info2.normal, POS_Y_AXIS)),
				Math.abs(BABYLON.Vector3.Dot(info2.normal, POS_Z_AXIS)),
			)*info2.area;

			return fMaxDot2 - fMaxDot1;
		});

		// See if we can append any of the adjacent faces to our group
		let currGroupInfo = null;
		let allGroupInfos = [];
		let traversedFaces = new Set();
		for(let startFaceInfo of sortedFaceArray)
		{
			if(traversedFaces.has(startFaceInfo.index))
				continue;

			// If we don't have a group yet, start with this face:
			currGroupInfo = {
				averageNormalsSum: startFaceInfo.normal.scale(startFaceInfo.area),
				averageWeightsSum: startFaceInfo.area,
				averageNormal: startFaceInfo.normal.clone(),
				faceInfos: [startFaceInfo],
			};
			traversedFaces.add(startFaceInfo.index);
			allGroupInfos.push(currGroupInfo);

			// See if we can append any of the adjacent faces to our group
			let iNumAppended;
			do
			{
				// We need to find adjacent faces to any possible face in currently in the group.
				// Then I'm not sure we can have the concept of a "start" face. Only perhaps the average normal for the group?
				// Or max variance between all normals?
				// For each face, go over its edges and find adjacent face
				iNumAppended = 0;
				const iNumFaceInfos = currGroupInfo.faceInfos.length;
				for(let iFaceInfo = 0; iFaceInfo < iNumFaceInfos; iFaceInfo++)
				{
					let faceInfo = currGroupInfo.faceInfos[iFaceInfo];

					let allAdjacentFaces = [...faceInfo.adjacentFaces[0], ...faceInfo.adjacentFaces[1], ...faceInfo.adjacentFaces[2]];
					for(let adjacentInfoWithOverlap of allAdjacentFaces)
					{
						let adjacentInfo = adjacentInfoWithOverlap.info;
						if(traversedFaces.has(adjacentInfo.index))
							continue;

						// Otherwise, see what the angle between the last face normal is,
						// as well as between the start normal
						let fDotToAvgNormal = BABYLON.Vector3.Dot(currGroupInfo.averageNormal, adjacentInfo.normal);
						let fDotToSourceFace = BABYLON.Vector3.Dot(faceInfo.normal, adjacentInfo.normal);

						if(fDotToSourceFace >= fLastMaxFaceAngleCos && fDotToAvgNormal >= fAverageNormalMaxFaceAngleCos)
						{
							// Add to existing group
							// Update our average normal
							currGroupInfo.averageNormalsSum.addInPlace(adjacentInfo.normal.scale(adjacentInfo.area));
							currGroupInfo.averageWeightsSum += adjacentInfo.area;
							currGroupInfo.averageNormal = currGroupInfo.averageNormalsSum.scale(1.0/adjacentInfo.area);
							currGroupInfo.averageNormal.normalize();

							currGroupInfo.faceInfos.push(adjacentInfo);
							traversedFaces.add(adjacentInfo.index);
							iNumAppended++;
						}					
					}
				}
			}
			while(iNumAppended > 0);
		}

		// Now, make sure we add any individual faces that were not part of any groups:
		//pMeshHelper.iterateOverMeshFaces((index, vecWorldPoints, vecFaceNormal, faceArea) => {
		for(let faceInfo of sortedFaceArray)
		{
			if(faceInfo.area < MIN_TEST_FACE_AREA || traversedFaces.has(faceInfo.index))
				continue;

			// If we don't have a group yet, start with this face:
			currGroupInfo = {
				averageNormalsSum: faceInfo.normal.scale(faceInfo.area),
				averageWeightsSum: faceInfo.area,
				averageNormal: faceInfo.normal.clone(),
				faceInfos: [faceInfo],
			};
			traversedFaces.add(faceInfo.index);
			allGroupInfos.push(currGroupInfo);
		}

		return allGroupInfos;
	}

	getTrianglesOnPlaneWithinPolygon(vecPoints, fPlaneDistEpsilon = EPSILON, optExcludeSet = null, excludeUnaligned = false)
	{
		if(vecPoints.length < 3)
			return { faceInfos: [], normal: BABYLON.Vector3.Zero() };

		let relCenter = vecPoints[0];
		let plane = BABYLON.Plane.FromPoints(vecPoints[0], vecPoints[1], vecPoints[2]);
		let projectedTargetPoly2D = GeoUtils.projectPointsOntoPlane(vecPoints, plane.normal, relCenter);

		let vecSideDir;
		let sideDirections = [];
		let sideLengths = [];
		for(let pointIndex = 0; pointIndex < vecPoints.length; pointIndex++)
		{
			let iNextPoint = (pointIndex + 1)%vecPoints.length;
			vecSideDir = vecPoints[iNextPoint].subtract(vecPoints[pointIndex]);
			sideLengths.push(vecSideDir.length());
			vecSideDir.normalize();
			sideDirections.push(vecSideDir);
		}

		let meshFaces = this.meshFaces;
		if(this.faceVoxelGrid)
		{
			let bbox = Utils3D.computePointsBBox(vecPoints);
			bbox.min.addInPlaceFromFloats(-fPlaneDistEpsilon, -fPlaneDistEpsilon, -fPlaneDistEpsilon);
			bbox.max.addInPlaceFromFloats(fPlaneDistEpsilon, fPlaneDistEpsilon, fPlaneDistEpsilon);
			meshFaces = this.faceVoxelGrid.getObjectsForSegment(bbox.min, bbox.max);
		}			

		let largestArea = -Number.MAX_VALUE;
		let largestFace = null;
		let vertexIdx;
		let resFaces = [];
		const funcDotProd = BABYLON.Vector3.Dot;
		for(let faceInfo of meshFaces)
		{
			if(optExcludeSet && optExcludeSet.has(faceInfo.index))
				continue;

			if (excludeUnaligned && funcDotProd(faceInfo.normal, plane.normal) <= 0) {
				continue;
			}

			for(vertexIdx = 0; vertexIdx < faceInfo.globalVerts.length; vertexIdx++)
			{
				let fDist = funcDotProd(faceInfo.globalVerts[vertexIdx], plane.normal) + plane.d;
				if(Math.abs(fDist) > fPlaneDistEpsilon)
					break;
			}

			// If we're not all on the same plane, skip this face
			if(vertexIdx < faceInfo.globalVerts.length)
				continue;

			// Project onto the plane:
			let bAreAllPointsInside = true;
			let faceVerts2D = GeoUtils.projectPointsOntoPlane(faceInfo.globalVerts, plane.normal, relCenter);
			for(let index = 0; index < faceVerts2D.length; index++)
			{
				// See if this face vertex lies on one of the edges of the region (or close to it)
				let isOneTheEdge = false;
				for(let regionPointIndex = 0; regionPointIndex < vecPoints.length; regionPointIndex++)
				{
					if(Utils3D.doesSegmentContainPoint(vecPoints[regionPointIndex], sideDirections[regionPointIndex], 
						sideLengths[regionPointIndex], faceInfo.globalVerts[index], fPlaneDistEpsilon))
					{
						isOneTheEdge = true;
						break;
					}
				}

				if(isOneTheEdge)
					continue;

				if(!Utils2D.isPointInPoly(projectedTargetPoly2D, faceVerts2D[index]))
				{
					bAreAllPointsInside = false;
					break;
				}
			}

			if(!bAreAllPointsInside)
				continue;

			// Add face
			resFaces.push(faceInfo);

			if(faceInfo.area > largestArea)
			{
				largestArea = faceInfo.area;
				largestFace = faceInfo;
			}
		}

		// We need to ensure that the normal direction is correct; if any have any faces, find the largest
		// one and compare that:
		let finalNormal = plane.normal;
		if(largestFace && BABYLON.Vector3.Dot(largestFace.normal, plane.normal) < 0)
			finalNormal.scaleInPlace(-1);

		return { faceInfos: resFaces, normal: finalNormal, planePoint: plane.normal.scale(-plane.d) };
	}
}

// This is so that we can examine meshes from the console
window.MeshInfo = MeshInfo;